/**
 * Jovii — REVEAL ON SCROLL, as a Svelte action.
 *
 * `use:reveal` on any element: it starts translated down and transparent, and
 * settles as it comes into view. One IntersectionObserver per element, torn
 * down on destroy, and it fires ONCE — an element that re-animates every time
 * it crosses the fold is a page that will not sit still to be read.
 *
 * REDUCED MOTION SKIPS THE WHOLE THING, and not by shortening it: the element
 * is marked shown immediately and never gets a transform. The house rule.
 *
 * NO JAVASCRIPT, NO PROBLEM: the hidden state lives behind `[data-reveal]`,
 * which only this action sets, so a page that never runs the action renders
 * completely visible rather than blank.
 */
export function reveal(node: HTMLElement, delayMs = 0): { destroy(): void } {
  if (typeof IntersectionObserver === 'undefined') return { destroy() {} };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion) return { destroy() {} };

  node.dataset.reveal = 'hidden';
  if (delayMs > 0) node.style.setProperty('--reveal-delay', `${delayMs}ms`);

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        node.dataset.reveal = 'shown';
        io.disconnect();
      }
    },
    /* A little before it is fully in view, so the motion finishes about when
       the reader's eye arrives rather than starting then. */
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );
  io.observe(node);

  return {
    destroy() {
      io.disconnect();
    }
  };
}
